import React, {useEffect, useMemo, useRef, useState} from "react";
import {Chart as ChartJS, registerables} from "chart.js";
import {Model, isEmpty, Client, getCookie} from "weed-js";
import {useSelector} from "react-redux";
import {DatePicker} from "@mui/x-date-pickers/DatePicker/DatePicker.js";
import {
    LocalizationProvider
} from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider.js";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs/AdapterDayjs.js";
import dayjs from "dayjs";
import {Table} from "../../../../../../components/table/Table";
import {Loading} from "../../../../../../components/elements/Loading";
import {ColumnDef} from "../../../../../../components/table/classes/ColumnDef.js";
import {SaveReportView} from "../../../../../../components/forms/SaveReportView";

import {
    Item,
    Transaction,
    Inventory,
    DutchieAccount,
    Vendor,
    Product,
    Category,
    Brand,
    Producer, Employee, Customer, MasterCategory, Strain, Package,
} from "../../../../../../models/dutchie.js";

import {
    View as ReportView
} from "../../../../../../models/sso.js";

import {
    MetrcAccount,
    Item as MetrcTransactionItem,
    InventoryItem as MetrcInventoryItem,
    Receipt as MetrcReceipt,
    Employee as MetrcEmployee,
    Brand as MetrcBrand,
    Strain as MetrcStrain,
    Transaction as MetrcTransaction,
    Package as MetrcPackage,
    Location as MetrcLocation,
    LocationType as MetrcLocationType,
    Product as MetrcProduct,
    ProductCategory as MetrcProductCategory,
    ProductCategoryType as MetrcProductCategoryType,
    Facility as MetrcFacility,
    PackageWholesalePrice as MetrcPackageWholesalePrice,
    Transfer as MetrcTransfer,
    Delivery as MetrcDelivery,
    DeliveredPackage as MetrcDeliveredPackage,
} from "../../../../../../models/metrc";
import {DATE, NUMBER, CURRENCY, STRING_LINK} from "../../../../../../components/table/constants";
import {Autocomplete, Grid} from "@mui/material";
import {DUTCHIE, METRC} from "../../../../../../constants";
import {
    CanonicalOrganization,
    CanonicalIntegrationAccount,
    CanonicalTransactionItem
} from "../../../../../../models/canonical";
import TextInput from "../../../../../../components/elements/TextInput";
import {View} from "../../../../../../components/table/classes/view/View.js";



ChartJS.register(...registerables);


const LoadingMessage = ({numLoading}) => {
    return <span style={{fontSize: "20px"}}>
        {`${(100 * (12 - numLoading) / 12).toFixed(0)}% complete`}
    </span>
}


const getDutchiePromises = (client, startDate, endDate, feedbackWrapper, selectedIntegrationAccounts, abortControllerRef) => {
    if (isEmpty(selectedIntegrationAccounts)) {
        throw new Error("No selected integration accounts");
    }
    const common = {
        client,
        startDate,
        endDate,
        abortController: abortControllerRef.current,
        dutchie_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
        cache: true
    }
    return [
        feedbackWrapper(DutchieAccount.fetch({
            client,
            params: {id__in: selectedIntegrationAccounts.map(a => a.id).join(",")},
            abortController: abortControllerRef.current
        })),
        feedbackWrapper(Customer.fetchByDate({...common})),
        feedbackWrapper(Brand.fetchByDate({...common})),
        feedbackWrapper(MasterCategory.fetchByDate({...common})),
        feedbackWrapper(Category.fetchByDate({...common})),
        feedbackWrapper(Strain.fetchByDate({...common})),
        feedbackWrapper(Vendor.fetchByDate({...common})),
        feedbackWrapper(Producer.fetchByDate({...common})),
        feedbackWrapper(Product.fetchByDate({...common})), //, items__transaction__dutchie_account_id__in: common.dutchie_account_id__in})),
        feedbackWrapper(Inventory.fetchByDate({...common})), // product__items__transaction__dutchie_account_id__in: common.dutchie_account_id__in})),
        feedbackWrapper(Transaction.fetchByDate({...common})),
        feedbackWrapper(Item.fetchByDate({...common})),//, transaction__dutchie_account_id__in: common.dutchie_account_id__in})),
        feedbackWrapper(Employee.fetchByDate({...common})),
        feedbackWrapper(Package.fetchByDate({...common})),
    ]
}

const executeDutchiePromises = (promises, setEmployees, setTransactions, setInventory, setProducts, setItems, setTotal) => {
    return Promise.all(promises).then(([
        fetchedAccounts,
        fetchedCustomers,
        fetchedBrands,
        fetchedMasterCategories,
        fetchedCategories,
        fetchedStrains,
        fetchedVendors,
        fetchedProducers,
        fetchedProducts,
        fetchedInventory,
        fetchedTransactions,
        fetchedItems,
        fetchedEmployees,
        fetchedPackages,
    ]) => {
        setEmployees(fetchedEmployees);
        setTransactions(fetchedTransactions);
        setInventory(fetchedInventory);
        setProducts(fetchedProducts);
        setTotal(prev => prev - promises.length);
        return fetchedItems;
    }).catch(err => {
        console.log("Error while executing dutchie promises:");
        console.error(err);
        setTotal(prev => prev - promises.length);
        return Promise.reject(err);
    })
}

const getMetrcPromises = (client, startDate, endDate, feedbackWrapper, selectedIntegrationAccounts, abortControllerRef) => {
    if (isEmpty(selectedIntegrationAccounts)) {
        throw new Error("No selected integration accounts");
    }
    const common = {
        client,
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
        abortController: abortControllerRef.current,
        metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
        cache: true,
    }

    const commonFetch = {
        client,
        params: {
            metrc_account_id__in: selectedIntegrationAccounts.map(a => a.id).join(","),
        },
        cache: true,
        abortController: abortControllerRef.current,
    }

    return [
        feedbackWrapper(MetrcAccount.fetch({
            client,
            params: {id__in: selectedIntegrationAccounts.map(a => a.id).join(",")},
            abortController: abortControllerRef.current
        })),
        feedbackWrapper(MetrcPackageWholesalePrice.fetchByDate({...common})),
        feedbackWrapper(MetrcReceipt.fetchByDate({...common})),
        feedbackWrapper(MetrcBrand.fetch({...commonFetch})),
        feedbackWrapper(MetrcFacility.fetch({...commonFetch})),
        feedbackWrapper(MetrcLocationType.fetch({...commonFetch})),
        feedbackWrapper(MetrcProductCategoryType.fetch({
            client,
            params: {limit: 100000},
            cache: true,
            abortController: abortControllerRef.current
        })),
        feedbackWrapper(MetrcProductCategory.fetch({
            client,
            params: {limit: 100000},
            cache: true,
            abortController: abortControllerRef.current
        })),
        feedbackWrapper(MetrcLocation.fetch({...commonFetch})),
        feedbackWrapper(MetrcEmployee.fetch({...commonFetch})),
        feedbackWrapper(MetrcStrain.fetchByDate({...common})),
        feedbackWrapper(MetrcPackage.fetchByDate({...common})),
        feedbackWrapper(MetrcProduct.fetchByDate({...common})),
        feedbackWrapper(MetrcInventoryItem.fetchByDate({...common})),
        feedbackWrapper(MetrcTransactionItem.fetchByDate({...common})),
        feedbackWrapper(MetrcTransaction.fetchByDate({...common})),
        feedbackWrapper(MetrcTransfer.fetchByDate({...common})),
        feedbackWrapper(MetrcDelivery.fetchByDate({...common})),
        feedbackWrapper(MetrcDeliveredPackage.fetchByDate({...common})),
    ]
}

const executeMetrcPromises = (promises, setEmployees, setTransactions, setInventory, setProducts, setItems, setTotal) => {
    return Promise.all(promises).then(([
        fetchMetrcAccounts,
        fetchedWholesalePrices,
        fetchedReceipts,
        fetchedBrands,
        fetchedFacilities,
        fetchedLocationTypes,
        fetchedProductCategoryTypes,
        fetchedProductCategories,
        fetchedLocations,
        fetchedEmployees,
        fetchedStrains,
        fetchedPackages,
        fetchedProducts,
        fetchedInventoryItems,
        fetchedTransactionItems,
        fetchedTransactions,
        fetchedTransfers,
        fetchedDeliveries,
        fetchedDeliveredPackages
    ]) => {
        setEmployees(fetchedEmployees);
        setTransactions(fetchedTransactions);
        setInventory(fetchedInventoryItems);
        setProducts(fetchedProducts);
        setTotal(prev => prev - promises.length);
        return fetchedTransactionItems;
    }).catch(err => {
        console.error(err);
        setTotal(prev => prev - promises.length);
    })
}


const RetailCostAndMargin = ({verticals, categories, availableViews}) => {

    const selectedIntegrationAccounts = useSelector(state => state.integrationReducer.accounts.selectedAccounts);
    const selectedIntegration = useSelector(state => state.integrationReducer.selectedIntegration);
    const selectedOrganization = useSelector(state => state.organizationReducer.selectedOrganization);

    const loggedInUser = useSelector(state => state.userReducer.loggedInUser);
    const [items, setItems] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [products, setProducts] = useState([])
    const [employees, setEmployees] = useState([]);
    const abortControllerRef = useRef(null);
    const [view, setView] = useState(null);
    const [newViews, setNewViews] = useState([]);
    const [errored, setErrored] = useState(false);
    const runIdRef = useRef(0);
    const client = useMemo(() => {
        return new Client();
    }, []);


    useEffect(() => {
        if (selectedOrganization?.id) {
            CanonicalOrganization.from({id: selectedOrganization?.id || 0, name: selectedOrganization?.name});
        }
    }, [selectedOrganization]);

    useEffect(() => {
        selectedIntegrationAccounts.map(account => {
            CanonicalIntegrationAccount.from({
                id: account.id,
                name: account.display_name,
                integration: account.namespace || account.integration || selectedIntegration,
                organization_id: account.organization_id,
            });
            if (selectedIntegration === METRC) {
                if (MetrcAccount.byId.has(account.id)) return MetrcAccount.byId.get(account.id);
                return new MetrcAccount(MetrcAccount.columns.map(column => account[column]));
            } else if (selectedIntegration === DUTCHIE) {
                if (DutchieAccount.byId.has(account.id)) return DutchieAccount.byId.get(account.id);
                return new DutchieAccount(DutchieAccount.columns.map(column => account[column]));
            }
        });
    }, [selectedIntegrationAccounts, selectedIntegration]);

    const [inProgress, setInProgress] = useState(0);
    const [total, setTotal] = useState(0);

    const [endDate, setEndDate] = useState(dayjs(new Date()));
    const [startDate, setStartDate] = useState(dayjs(new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate() - 7)));

    const accountIdsKey = useMemo(() => {
        return selectedIntegrationAccounts.map(a => a.id).sort().join(",");
    }, [selectedIntegrationAccounts]);

    const windowKey = useMemo(() => {
        return [startDate?.toString(), endDate?.toString()].join("|");
    }, [startDate, endDate]);


    const feedbackWrapper = (promise) => {
        return promise.then((input) => {
            setInProgress(prev => prev - 1);
            return input;
        }).catch(err => {
            setInProgress(prev => prev - 1);
            console.error("Error fetching data:", err);
            throw err;
        });
    }

    useEffect(() => {
        const runId = ++runIdRef.current;
        if (isEmpty(selectedIntegrationAccounts) || !startDate || !endDate || isEmpty(selectedIntegration)) {
            return;
        }

        const abortController = new AbortController();

        setErrored(false);
        let promises = [];

        if (selectedIntegration === DUTCHIE) {
            promises = getDutchiePromises(client, startDate, endDate, feedbackWrapper, selectedIntegrationAccounts, {current: abortController});
        } else if (selectedIntegration === METRC) {
            promises = getMetrcPromises(client, startDate, endDate, feedbackWrapper, selectedIntegrationAccounts, {current: abortController});
        }

        setTotal(prev => prev + promises.length);
        setInProgress(prev => prev + promises.length);

        let execute = null;
        if (selectedIntegration === DUTCHIE) {
            execute = executeDutchiePromises;
        } else if (selectedIntegration === METRC) {
            execute = executeMetrcPromises;
        } else {
            throw new Error("Unrecognized integration: " + selectedIntegration);
        }

        execute(promises, setEmployees, setTransactions, setInventory, setProducts, setItems, setTotal).then((fetchedItems) => {
            if (runId !== runIdRef.current) {
                return;
            }
            return Model.canonicalize().then(() => {
                if (runId !== runIdRef.current) {
                    return;
                }
                setItems(fetchedItems?.map(item => item.canonical?.[CanonicalTransactionItem.name]) || []);
            });
        }).catch(err => {
            setErrored(true);
            console.error(">>>>>>>>>> Error executing dutchie promises:", err);
        });

        return () => {
            abortController?.abort();
        };
    }, [
        accountIdsKey,
        windowKey,
        selectedIntegration,
    ]);

    const columnDefs = useMemo(() => {
        return [
            new ColumnDef({displayName: "Tx ID"}),
            new ColumnDef({displayName: "Item ID"}),
            new ColumnDef({displayName: "Employee ID"}),
            new ColumnDef({displayName: "Store"}),
            new ColumnDef({displayName: "Category"}),
            new ColumnDef({displayName: "Sub-Category"}),
            new ColumnDef({displayName: "Brand"}),
            new ColumnDef({displayName: "Vendor"}),
            new ColumnDef({displayName: "Product", type: STRING_LINK}),
            new ColumnDef({displayName: "SKU"}),
            new ColumnDef({displayName: "In Stock", type: NUMBER}),
            new ColumnDef({displayName: "Quantity Sold", type: NUMBER}),
            new ColumnDef({displayName: "Sale Total", type: CURRENCY}),
            new ColumnDef({displayName: "COGS", type: CURRENCY}),
            new ColumnDef({displayName: "Profit", type: CURRENCY}),
            new ColumnDef({displayName: "Unit Price", type: CURRENCY}),
            new ColumnDef({displayName: "Unit Cost", type: CURRENCY}),
            new ColumnDef({displayName: "Date", type: DATE})
        ]
    }, [accountIdsKey]);

    const activeStores = useMemo(() => {
        return new Set(selectedIntegrationAccounts.map(account => account.id));
    }, [accountIdsKey]);

    const data = useMemo(() => {
        const endAsDate = endDate.toDate();
        const startAsDate = startDate.toDate();
        return items.filter( item => {
            if (isEmpty(item)) {
                console.error("Item was undefined!", item, items)
                return false;
            }
            const txnDate = new Date(item.transaction?.transaction_date);
            const withinDateRange = (txnDate && txnDate <= endAsDate && txnDate >= startAsDate);
            const correctStore = item?.integration_account_id && activeStores.has(item.integration_account_id);
            return withinDateRange && correctStore;
        }).sort((a, b) => {
            const dateA = new Date(a.transaction?.transaction_date);
            const dateB = new Date(b.transaction?.transaction_date);
            return dateB - dateA; // Sort by transaction date ascending
        }).map(item => item.toRow());
    }, [items]);

    const getPercentComplete = (inProgress, total) => {
        if (total === 0) {
            return 0;
        }
        if (inProgress === 0) {
            return "100%";
        }
        if (total - inProgress <= 0) {
            return "0%";
        }
        return ((total - inProgress) / total * 100).toFixed(0) + "%";
    }

    const allViews = [...availableViews, ...newViews];

    return <>
        {inProgress > 0 ? <Loading fullscreen={true}
            title={"Finding Transactions " + getPercentComplete(inProgress, total)}/>
            :
            null
        }
        <Grid container spacing={12}>
            <Grid item xs={12} md={4}>
                <div className="control">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="Start Date" value={startDate}
                            onChange={(newDate) => {
                                setStartDate(dayjs(newDate));
                            }}/>
                    </LocalizationProvider>
                </div>
            </Grid>
            <Grid item xs={12} md={4}>
                <div className="control">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="End Date" value={endDate}
                            onChange={(newDate) => {
                                setEndDate(dayjs(newDate));
                            }}/>
                    </LocalizationProvider>
                </div>
            </Grid>
            <Grid item xs={12} md={4}>
                {!isEmpty(allViews) && <Autocomplete
                    className={"save-report-choose-vertical"}
                    filterSelectedOptions
                    id={"choose-vertical-dropdown"}
                    name={"choose-vertical-dropdown"}
                    role={"choose-vertical-dropdown"}
                    options={allViews.sort((a, b) => {
                        const first = [a.vertical?.name, a.category?.name, a.name].join(" » ");
                        const second = [a.vertical?.name, a.category?.name, a.name].join(" » ");
                        return first.localeCompare(second);
                    }).map(view => {
                        return {
                            label: [view.vertical?.name, view.category?.name, view.name].join(" » "),
                            value: view.id
                        }
                    })}
                    disablePortal={true}
                    isOptionEqualToValue={(option, value) => {
                        if (option?.value != null && value?.value != null) return option.value === value.value;
                        return (option?.label ?? "") === (value?.label ?? "");
                    }}
                    onChange={(event, option) => {
                        const report = ReportView.byId.get(option?.value) || null;
                        if (report) {
                            try {
                                if (typeof report.json !== "string") {
                                    setView(View.fromJSON(report.json));
                                } else {
                                    setView(View.fromJSON(JSON.parse(report.json)));
                                }
                            } catch (err) {
                                console.error("Error loading saved view:", err, option?.value, ReportView.byId?.get(option?.value));
                                setView(null);
                            }
                        } else {
                            setView(null);
                        }
                    }}
                    renderOption={(props, option) => {
                        return <li {...props} key={`choose-report-${option.value}`}>{option.label}</li>
                    }}
                    renderInput={(params) => (
                        <TextInput
                            {...params}
                            errors={{}}
                            formState={{}}
                            label="Load a Report"
                            placeholdler="---"
                        />
                    )}
                />}
            </Grid>
        </Grid>
        <Table
            columnDefs={columnDefs}
            data={data}
            enableSave={true}
            availableViews={allViews}
            savedView={view}
            setSavedView={setView}
            setActiveView={setView}
            noDataMessage={"No data available. Try adjusting your date range, or make sure you have an organization, integration, and store selected at the bottom of the page. If you're new here; check out the Integrations menu at the top of the page. Choose your integration, then click on the Sync Status tab. You can stay up to date on your data syncs there. When they complete, you should see some data. If not, please contact support; support@canold.com"}
            additionalActions={<SaveReportView
                loggedInUser={loggedInUser}
                selectedOrganization={selectedOrganization}
                selectedIntegration={selectedIntegration}
                currentView={view}
                verticals={verticals}
                categories={categories}
                setNewViews={setNewViews}
            />}
        />
    </>
};

export default RetailCostAndMargin;
